import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { 
  FileText, 
  ArrowRight, 
  CheckCircle2, 
  AlertTriangle, 
  Sparkles, 
  Eye, 
  EyeOff,
  ZoomIn,
  ZoomOut,
  ChevronLeft,
  ChevronRight,
  Info,
  Shield,
  Scale,
  Clock,
  DollarSign,
  X,
  Download,
  Loader2
} from 'lucide-react';
import jsPDF from 'jspdf';

// Simulated document content for demonstration
const generateDocumentContent = (isOriginal = true) => {
  if (isOriginal) {
    return {
      title: "SERVICE AGREEMENT",
      sections: [
        {
          id: 1,
          type: "header",
          content: "PROFESSIONAL SERVICES AGREEMENT",
          highlight: null
        },
        {
          id: 2,
          type: "paragraph",
          content: "This Agreement is entered into as of the date last signed below (the \"Effective Date\") by and between the parties identified herein.",
          highlight: null
        },
        {
          id: 3,
          type: "clause",
          title: "1. SERVICES",
          content: "The Service Provider shall provide the services described in Exhibit A attached hereto and incorporated herein by reference (the \"Services\").",
          highlight: null
        },
        {
          id: 4,
          type: "clause",
          title: "2. COMPENSATION",
          content: "In consideration for the Services, Client shall pay Service Provider the fees set forth in Exhibit B. Payment terms: Net 30 days from invoice date.",
          highlight: null
        },
        {
          id: 5,
          type: "clause",
          title: "3. TERM AND TERMINATION",
          content: "This Agreement shall commence on the Effective Date and continue for a period of twelve (12) months unless earlier terminated. Either party may terminate this Agreement upon thirty (30) days written notice.",
          highlight: null
        },
        {
          id: 6,
          type: "clause",
          title: "4. CONFIDENTIALITY",
          content: "Each party agrees to maintain in confidence all proprietary information received from the other party. This obligation shall survive termination of this Agreement.",
          highlight: null
        },
        {
          id: 7,
          type: "clause",
          title: "5. LIMITATION OF LIABILITY",
          content: "IN NO EVENT SHALL EITHER PARTY BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF THIS AGREEMENT.",
          highlight: null
        },
        {
          id: 8,
          type: "clause",
          title: "6. GOVERNING LAW",
          content: "This Agreement shall be governed by and construed in accordance with the laws of the State of Delaware, without regard to its conflict of laws principles.",
          highlight: null
        }
      ]
    };
  }
  
  // Analyzed/converted version with highlights
  return {
    title: "SERVICE AGREEMENT",
    sections: [
      {
        id: 1,
        type: "header",
        content: "PROFESSIONAL SERVICES AGREEMENT",
        highlight: null,
        formatting: "improved"
      },
      {
        id: 2,
        type: "paragraph",
        content: "This Agreement is entered into as of the date last signed below (the \"Effective Date\") by and between the parties identified herein.",
        highlight: null,
        formatting: "improved"
      },
      {
        id: 3,
        type: "clause",
        title: "1. SERVICES",
        content: "The Service Provider shall provide the services described in Exhibit A attached hereto and incorporated herein by reference (the \"Services\").",
        highlight: {
          type: "info",
          label: "Key Clause",
          note: "Defines scope of work - ensure Exhibit A is detailed"
        },
        formatting: "improved"
      },
      {
        id: 4,
        type: "clause",
        title: "2. COMPENSATION",
        content: "In consideration for the Services, Client shall pay Service Provider the fees set forth in Exhibit B. Payment terms: Net 30 days from invoice date.",
        highlight: {
          type: "attention",
          label: "Payment Terms",
          note: "Net 30 days is standard. Consider negotiating Net 15 for better cash flow."
        },
        formatting: "improved"
      },
      {
        id: 5,
        type: "clause",
        title: "3. TERM AND TERMINATION",
        content: "This Agreement shall commence on the Effective Date and continue for a period of twelve (12) months unless earlier terminated. Either party may terminate this Agreement upon thirty (30) days written notice.",
        highlight: {
          type: "warning",
          label: "Termination Risk",
          note: "30-day notice period may be short. Consider 60-90 days for better protection."
        },
        formatting: "improved"
      },
      {
        id: 6,
        type: "clause",
        title: "4. CONFIDENTIALITY",
        content: "Each party agrees to maintain in confidence all proprietary information received from the other party. This obligation shall survive termination of this Agreement.",
        highlight: {
          type: "success",
          label: "Well Protected",
          note: "Standard confidentiality clause with survival provision. Good protection."
        },
        formatting: "improved"
      },
      {
        id: 7,
        type: "clause",
        title: "5. LIMITATION OF LIABILITY",
        content: "IN NO EVENT SHALL EITHER PARTY BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF THIS AGREEMENT.",
        highlight: {
          type: "critical",
          label: "Critical Clause",
          note: "Caps your ability to recover damages. Consider negotiating a cap amount or carve-outs for gross negligence."
        },
        formatting: "improved"
      },
      {
        id: 8,
        type: "clause",
        title: "6. GOVERNING LAW",
        content: "This Agreement shall be governed by and construed in accordance with the laws of the State of Delaware, without regard to its conflict of laws principles.",
        highlight: {
          type: "info",
          label: "Jurisdiction",
          note: "Delaware law is business-friendly. Verify this aligns with your business location."
        },
        formatting: "improved"
      }
    ]
  };
};

// Analysis summary data
const analysisHighlights = {
  totalClauses: 8,
  keyFindings: 4,
  riskAreas: 2,
  improvements: 6,
  overallScore: 78
};

const DocumentComparison = ({ 
  originalFile, 
  conversionResult, 
  analysisResult,
  onClose 
}) => {
  const [showHighlights, setShowHighlights] = useState(true);
  const [activeSection, setActiveSection] = useState(null);
  const [zoom, setZoom] = useState(100);
  const [viewMode, setViewMode] = useState('side-by-side'); // 'side-by-side', 'overlay', 'single'
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  
  const originalDoc = generateDocumentContent(true);
  const analyzedDoc = generateDocumentContent(false);

  // PDF Report Generation Function
  const generatePDFReport = async () => {
    setIsGeneratingPDF(true);
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - (margin * 2);
      let yPos = margin;
      
      // Helper function to add new page if needed
      const checkPageBreak = (requiredHeight) => {
        if (yPos + requiredHeight > pageHeight - margin) {
          pdf.addPage();
          yPos = margin;
          return true;
        }
        return false;
      };
      
      // Helper to wrap text
      const addWrappedText = (text, x, y, maxWidth, lineHeight = 5) => {
        const lines = pdf.splitTextToSize(text, maxWidth);
        lines.forEach((line, index) => {
          checkPageBreak(lineHeight);
          pdf.text(line, x, yPos);
          yPos += lineHeight;
        });
        return yPos;
      };
      
      // ===== COVER PAGE =====
      // Header gradient effect (simulated with rectangles)
      pdf.setFillColor(59, 130, 246); // Blue
      pdf.rect(0, 0, pageWidth, 60, 'F');
      
      // Logo area
      pdf.setFillColor(255, 255, 255);
      pdf.roundedRect(margin, 15, 12, 12, 2, 2, 'F');
      pdf.setFillColor(59, 130, 246);
      pdf.setFontSize(8);
      pdf.setTextColor(59, 130, 246);
      pdf.text('DOC', margin + 1.5, 23);
      
      // Title
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Document Analysis Report', margin + 18, 25);
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      pdf.text('AI-Powered Legal Document Comparison', margin + 18, 33);
      
      // Document info box
      yPos = 75;
      pdf.setFillColor(248, 250, 252);
      pdf.roundedRect(margin, yPos, contentWidth, 35, 3, 3, 'F');
      
      pdf.setTextColor(71, 85, 105);
      pdf.setFontSize(10);
      pdf.text('Document:', margin + 5, yPos + 10);
      pdf.text('Generated:', margin + 5, yPos + 18);
      pdf.text('Analysis Type:', margin + 5, yPos + 26);
      
      pdf.setTextColor(15, 23, 42);
      pdf.setFont('helvetica', 'bold');
      pdf.text(originalFile?.name || 'contract.pdf', margin + 35, yPos + 10);
      pdf.text(new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }), margin + 35, yPos + 18);
      pdf.text('Full Legal Analysis with AI Insights', margin + 35, yPos + 26);
      
      // ===== EXECUTIVE SUMMARY =====
      yPos = 125;
      pdf.setFillColor(239, 246, 255);
      pdf.roundedRect(margin, yPos, contentWidth, 50, 3, 3, 'F');
      
      pdf.setTextColor(30, 64, 175);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Executive Summary', margin + 5, yPos + 12);
      
      pdf.setTextColor(55, 65, 81);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      yPos += 20;
      addWrappedText(
        'This report provides a comprehensive before/after comparison of your legal document with AI-powered analysis. The document has been analyzed for key clauses, potential risks, compliance issues, and areas requiring attention.',
        margin + 5, yPos, contentWidth - 10, 5
      );
      
      // ===== ANALYSIS METRICS =====
      yPos += 20;
      pdf.setTextColor(15, 23, 42);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Analysis Metrics', margin, yPos);
      yPos += 10;
      
      // Metrics boxes
      const boxWidth = (contentWidth - 15) / 4;
      const metrics = [
        { label: 'Total Clauses', value: analysisHighlights.totalClauses, color: [71, 85, 105] },
        { label: 'Key Findings', value: analysisHighlights.keyFindings, color: [59, 130, 246] },
        { label: 'Risk Areas', value: analysisHighlights.riskAreas, color: [245, 158, 11] },
        { label: 'Overall Score', value: `${analysisHighlights.overallScore}%`, color: [34, 197, 94] }
      ];
      
      metrics.forEach((metric, index) => {
        const x = margin + (index * (boxWidth + 5));
        pdf.setFillColor(248, 250, 252);
        pdf.roundedRect(x, yPos, boxWidth, 25, 2, 2, 'F');
        
        pdf.setTextColor(...metric.color);
        pdf.setFontSize(18);
        pdf.setFont('helvetica', 'bold');
        pdf.text(String(metric.value), x + boxWidth/2, yPos + 12, { align: 'center' });
        
        pdf.setTextColor(100, 116, 139);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.text(metric.label, x + boxWidth/2, yPos + 20, { align: 'center' });
      });
      
      // ===== DETAILED FINDINGS =====
      yPos += 40;
      checkPageBreak(20);
      
      pdf.setTextColor(15, 23, 42);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Detailed Clause Analysis', margin, yPos);
      yPos += 10;
      
      // Clause findings
      const findings = analyzedDoc.sections.filter(s => s.highlight);
      findings.forEach((section, index) => {
        checkPageBreak(40);
        
        // Risk level color
        const colors = {
          critical: [239, 68, 68],
          warning: [245, 158, 11],
          attention: [59, 130, 246],
          success: [34, 197, 94],
          info: [99, 102, 241]
        };
        const color = colors[section.highlight.type] || colors.info;
        
        // Section box
        pdf.setFillColor(255, 255, 255);
        pdf.setDrawColor(...color);
        pdf.setLineWidth(0.5);
        pdf.roundedRect(margin, yPos, contentWidth, 35, 2, 2, 'FD');
        
        // Left border accent
        pdf.setFillColor(...color);
        pdf.rect(margin, yPos, 3, 35, 'F');
        
        // Badge
        pdf.setFillColor(...color, 0.1);
        pdf.roundedRect(margin + 8, yPos + 4, 25, 6, 1, 1, 'F');
        pdf.setTextColor(...color);
        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'bold');
        pdf.text(section.highlight.label.toUpperCase(), margin + 10, yPos + 8.5);
        
        // Title
        pdf.setTextColor(15, 23, 42);
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold');
        pdf.text(section.title, margin + 8, yPos + 18);
        
        // AI Insight
        pdf.setTextColor(71, 85, 105);
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        const insightLines = pdf.splitTextToSize(`AI Insight: ${section.highlight.note}`, contentWidth - 16);
        pdf.text(insightLines.slice(0, 2), margin + 8, yPos + 26);
        
        yPos += 40;
      });
      
      // ===== RECOMMENDATIONS =====
      checkPageBreak(60);
      yPos += 5;
      
      pdf.setFillColor(240, 253, 244);
      pdf.roundedRect(margin, yPos, contentWidth, 45, 3, 3, 'F');
      
      pdf.setTextColor(22, 101, 52);
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Recommendations', margin + 5, yPos + 12);
      
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      const recommendations = [
        '• Review and negotiate the Limitation of Liability clause before signing',
        '• Consider extending the termination notice period to 60-90 days',
        '• Ensure Exhibit A contains detailed service descriptions',
        '• Verify Delaware jurisdiction aligns with your business needs'
      ];
      
      let recY = yPos + 20;
      recommendations.forEach(rec => {
        pdf.text(rec, margin + 5, recY);
        recY += 6;
      });
      
      // ===== FOOTER =====
      yPos = pageHeight - 25;
      pdf.setDrawColor(226, 232, 240);
      pdf.setLineWidth(0.3);
      pdf.line(margin, yPos, pageWidth - margin, yPos);
      
      pdf.setTextColor(148, 163, 184);
      pdf.setFontSize(8);
      pdf.text('Generated by LegalDocConverter - AI-Powered Document Analysis', margin, yPos + 8);
      pdf.text('This analysis is for informational purposes only. Consult a legal professional for advice.', margin, yPos + 13);
      pdf.text(`Page 1 of ${pdf.getNumberOfPages()}`, pageWidth - margin - 15, yPos + 8);
      
      // Save the PDF
      const fileName = `Document_Analysis_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const getHighlightStyle = (highlight) => {
    if (!highlight || !showHighlights) return {};
    
    switch (highlight.type) {
      case 'critical':
        return {
          borderLeft: '4px solid #ef4444',
          backgroundColor: 'rgba(239, 68, 68, 0.08)',
        };
      case 'warning':
        return {
          borderLeft: '4px solid #f59e0b',
          backgroundColor: 'rgba(245, 158, 11, 0.08)',
        };
      case 'attention':
        return {
          borderLeft: '4px solid #3b82f6',
          backgroundColor: 'rgba(59, 130, 246, 0.08)',
        };
      case 'success':
        return {
          borderLeft: '4px solid #22c55e',
          backgroundColor: 'rgba(34, 197, 94, 0.08)',
        };
      case 'info':
      default:
        return {
          borderLeft: '4px solid #6366f1',
          backgroundColor: 'rgba(99, 102, 241, 0.08)',
        };
    }
  };

  const getHighlightBadge = (highlight) => {
    if (!highlight || !showHighlights) return null;
    
    const colors = {
      critical: 'bg-red-100 text-red-700 border-red-200',
      warning: 'bg-amber-100 text-amber-700 border-amber-200',
      attention: 'bg-blue-100 text-blue-700 border-blue-200',
      success: 'bg-green-100 text-green-700 border-green-200',
      info: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    };
    
    const icons = {
      critical: <AlertTriangle className="w-3 h-3" />,
      warning: <AlertTriangle className="w-3 h-3" />,
      attention: <Info className="w-3 h-3" />,
      success: <CheckCircle2 className="w-3 h-3" />,
      info: <Info className="w-3 h-3" />,
    };
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${colors[highlight.type]}`}>
        {icons[highlight.type]}
        {highlight.label}
      </span>
    );
  };

  const renderSection = (section, isAnalyzed = false) => {
    const highlight = isAnalyzed ? section.highlight : null;
    const isActive = activeSection === section.id;
    
    return (
      <div
        key={section.id}
        className={`p-4 rounded-lg transition-all duration-300 cursor-pointer ${
          isActive ? 'ring-2 ring-blue-400' : ''
        } ${isAnalyzed && section.formatting === 'improved' ? 'bg-white' : 'bg-slate-50'}`}
        style={isAnalyzed ? getHighlightStyle(highlight) : {}}
        onClick={() => setActiveSection(isActive ? null : section.id)}
        data-testid={`doc-section-${section.id}-${isAnalyzed ? 'analyzed' : 'original'}`}
      >
        {section.type === 'header' && (
          <h3 className="text-lg font-bold text-slate-900 text-center mb-2">
            {section.content}
          </h3>
        )}
        
        {section.type === 'paragraph' && (
          <p className="text-sm text-slate-700 leading-relaxed">
            {section.content}
          </p>
        )}
        
        {section.type === 'clause' && (
          <div>
            <div className="flex items-start justify-between mb-2">
              <h4 className="font-semibold text-slate-800">{section.title}</h4>
              {isAnalyzed && getHighlightBadge(highlight)}
            </div>
            <p className="text-sm text-slate-700 leading-relaxed">
              {section.content}
            </p>
            {isAnalyzed && highlight && showHighlights && (
              <div className="mt-3 p-3 bg-white/80 rounded-lg border border-slate-200">
                <p className="text-xs text-slate-600 flex items-start gap-2">
                  <Sparkles className="w-3 h-3 mt-0.5 text-indigo-500 flex-shrink-0" />
                  <span><strong>AI Insight:</strong> {highlight.note}</span>
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="document-comparison-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold">Document Comparison</h2>
              <p className="text-blue-100 text-sm">Before & After Analysis View</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* View Mode Toggle */}
            <div className="flex items-center bg-white/20 rounded-lg p-1">
              <button
                onClick={() => setViewMode('side-by-side')}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  viewMode === 'side-by-side' ? 'bg-white text-blue-600' : 'text-white hover:bg-white/10'
                }`}
              >
                Side by Side
              </button>
              <button
                onClick={() => setViewMode('single')}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  viewMode === 'single' ? 'bg-white text-blue-600' : 'text-white hover:bg-white/10'
                }`}
              >
                Single View
              </button>
            </div>
            
            {/* Highlight Toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowHighlights(!showHighlights)}
              className="text-white hover:bg-white/20"
              data-testid="toggle-highlights-btn"
            >
              {showHighlights ? <Eye className="w-4 h-4 mr-1" /> : <EyeOff className="w-4 h-4 mr-1" />}
              {showHighlights ? 'Hide' : 'Show'} Highlights
            </Button>
            
            {/* Zoom Controls */}
            <div className="flex items-center gap-1 bg-white/20 rounded-lg px-2">
              <button 
                onClick={() => setZoom(Math.max(50, zoom - 10))}
                className="p-1 hover:bg-white/20 rounded"
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              <span className="text-sm w-12 text-center">{zoom}%</span>
              <button 
                onClick={() => setZoom(Math.min(150, zoom + 10))}
                className="p-1 hover:bg-white/20 rounded"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
            </div>
            
            {/* Generate Report Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={generatePDFReport}
              disabled={isGeneratingPDF}
              className="text-white hover:bg-white/20 bg-white/10"
              data-testid="generate-report-btn"
            >
              {isGeneratingPDF ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-1" />
                  Export Report
                </>
              )}
            </Button>
            
            {/* Close Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-white hover:bg-white/20"
              data-testid="close-comparison-btn"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>
        
        {/* Analysis Summary Bar */}
        <div className="bg-gradient-to-r from-slate-50 to-blue-50 border-b border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FileText className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Document</p>
                  <p className="font-medium text-slate-900">{originalFile?.name || 'contract.pdf'}</p>
                </div>
              </div>
              
              <div className="h-8 w-px bg-slate-300" />
              
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-slate-900">{analysisHighlights.totalClauses}</p>
                  <p className="text-xs text-slate-500">Clauses</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">{analysisHighlights.keyFindings}</p>
                  <p className="text-xs text-slate-500">Key Findings</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-amber-600">{analysisHighlights.riskAreas}</p>
                  <p className="text-xs text-slate-500">Risk Areas</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{analysisHighlights.improvements}</p>
                  <p className="text-xs text-slate-500">Improvements</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="text-xs text-slate-500">Overall Score</p>
                <p className="text-2xl font-bold text-green-600">{analysisHighlights.overallScore}%</p>
              </div>
              <div className="w-20 h-20 relative">
                <svg className="w-20 h-20 transform -rotate-90">
                  <circle
                    cx="40"
                    cy="40"
                    r="35"
                    fill="none"
                    stroke="#e2e8f0"
                    strokeWidth="6"
                  />
                  <circle
                    cx="40"
                    cy="40"
                    r="35"
                    fill="none"
                    stroke="#22c55e"
                    strokeWidth="6"
                    strokeDasharray={`${(analysisHighlights.overallScore / 100) * 220} 220`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-green-500" />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Document Comparison Content */}
        <div className="flex-1 overflow-hidden">
          {viewMode === 'side-by-side' ? (
            <div className="grid grid-cols-2 h-full divide-x divide-slate-200">
              {/* Original Document */}
              <div className="flex flex-col h-full">
                <div className="bg-slate-100 px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-slate-400 rounded-full" />
                    <span className="font-medium text-slate-700">Original Document</span>
                  </div>
                  <span className="text-xs text-slate-500">Before conversion</span>
                </div>
                <div 
                  className="flex-1 overflow-y-auto p-4 bg-slate-50"
                  style={{ fontSize: `${zoom}%` }}
                >
                  <div className="max-w-xl mx-auto space-y-3">
                    {originalDoc.sections.map(section => renderSection(section, false))}
                  </div>
                </div>
              </div>
              
              {/* Analyzed Document */}
              <div className="flex flex-col h-full">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 border-b border-blue-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full animate-pulse" />
                    <span className="font-medium text-blue-800">Analyzed & Enhanced</span>
                    <Sparkles className="w-4 h-4 text-indigo-500" />
                  </div>
                  <span className="text-xs text-blue-600">AI-powered insights</span>
                </div>
                <div 
                  className="flex-1 overflow-y-auto p-4 bg-white"
                  style={{ fontSize: `${zoom}%` }}
                >
                  <div className="max-w-xl mx-auto space-y-3">
                    {analyzedDoc.sections.map(section => renderSection(section, true))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Single View Mode */
            <div className="h-full flex flex-col">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 border-b border-blue-200 flex items-center justify-center gap-3">
                <Sparkles className="w-4 h-4 text-indigo-500" />
                <span className="font-medium text-blue-800">Analyzed Document with AI Insights</span>
              </div>
              <div 
                className="flex-1 overflow-y-auto p-6 bg-white"
                style={{ fontSize: `${zoom}%` }}
              >
                <div className="max-w-2xl mx-auto space-y-4">
                  {analyzedDoc.sections.map(section => renderSection(section, true))}
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Footer Legend */}
        <div className="bg-slate-50 border-t border-slate-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 text-xs">
              <span className="text-slate-500 font-medium">Legend:</span>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-red-400 rounded" />
                <span className="text-slate-600">Critical</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-amber-400 rounded" />
                <span className="text-slate-600">Warning</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-blue-400 rounded" />
                <span className="text-slate-600">Attention</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-green-400 rounded" />
                <span className="text-slate-600">Good</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-indigo-400 rounded" />
                <span className="text-slate-600">Info</span>
              </div>
            </div>
            
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Shield className="w-4 h-4" />
              <span>AI analysis is for informational purposes. Always consult a legal professional for advice.</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentComparison;
